import { useEffect, useRef, useState } from 'react'
import { vatZeichen, vatSatz, normalizeVatId } from '../vatMark'
import {
    getAuthor, getAuthorIban, updateAuthor, checkAuthorVat, checkIbanFormat, getBlockedPeriod,
    type AuthorDetail, type FeeType, type BlockedPeriod, type VatCheckOutcome,
} from '../api'
import { useTabs, useDataVersion, useDirtyGuard, stableJson, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { useEditLock } from '../layout/useEditLock'
import InfoTip from '../components/InfoTip'
import { formatMoney, moneySign } from '../lookups'
import CountryPicker from '../components/CountryPicker'
import { plural, fill, SALE_STATE_LABELS, AUTHOR_SALUTATION_LABELS } from '../../shared/texts'
import { AUTHOR_SALUTATIONS, AUTHOR_TITLE_MAX_LENGTH, type AuthorSalutation } from '../../shared/constants'
import ConfirmDialog from '../components/ConfirmDialog'
import { checkBeforeSave } from '../forms/checkForm'
import { PAGES_AUTHOR_FORM_TEXTS as T } from '../../shared/texts'

// The author master data, as a TAB (it used to be a drawer inside the list). The move matters
// for more than looks: a drawer was gone the moment you looked elsewhere, a tab stays open and
// keeps unsaved input — so this mask registers with the dirty guard like every other form tab
// (● on the tab, counted in the header, asked about on close and on reload).

const empty = {
    salutation: '' as '' | AuthorSalutation, title: '',
    displayName: '', company: '', email: '',
    address: { line1: '', line2: '', postalCode: '', city: '', country: '' },
    vatId: '',
    // Blank on purpose and only overwritten when typed — same contract as the secrets in the
    // settings, so re-saving does not wipe the stored one.
    iban: '', bic: '', accountHolder: '',
    // The number fields are kept as TEXT while editing. `Number('')` is 0, so a field that is
    // emptied and typed again would otherwise compare equal to something it is not — and an
    // emptied one would travel as NaN.
    feeType: '' as '' | FeeType, feeValue: '', payoutThreshold: '',
    status: 'active' as 'active' | 'blocked',
    notes: '',
}
type Form = typeof empty

function toForm(a: AuthorDetail): Form {
    return {
        salutation: a.salutation ?? '', title: a.title ?? '',
        displayName: a.displayName, company: a.company ?? '', email: a.email ?? '',
        address: {
            line1: a.address.line1 ?? '', line2: a.address.line2 ?? '',
            postalCode: a.address.postalCode ?? '', city: a.address.city ?? '',
            country: a.address.country ?? '',
        },
        vatId: a.vatId ?? '',
        iban: '', bic: a.bic ?? '', accountHolder: a.accountHolder ?? '',
        feeType: a.feeType ?? '',
        feeValue: a.feeValue == null ? '' : String(a.feeValue),
        payoutThreshold: a.payoutThreshold == null ? '' : String(a.payoutThreshold),
        // Ein Altbestands-'paused' wird als 'blocked' angezeigt — es MEINT dasselbe, nur
        // ohne Zeitpunkt. Speichern setzt ihn dann (der Server tut das, nicht das Formular).
        status: a.status === 'active' ? 'active' : 'blocked',
        notes: a.notes ?? '',
    }
}

// What „geblockt" actually does, in one line under the field — because the whole point of the
// rename (operator, 08.09.2026: „Vielleicht macht es mehr Sinn das Feld geblockt zu benennen")
// was that the old „pausiert" hid a money consequence behind a harmless word.
function statusHint(status: 'active' | 'blocked', rec: AuthorDetail | null): string {
    if (status === 'active') return T.statusActiveHint
    if (rec?.blockedAt) {
        return fill(T.statusBlockedSince, { date: new Date(rec.blockedAt).toLocaleDateString('de-DE') })
    }
    if (rec && rec.status !== 'active') {
        return T.statusBlockedNoDate
    }
    return T.statusBlockFuture
}

export default function AuthorForm({ tabId, id }: { tabId?: string, id?: string }) {
    const formRef = useRef<HTMLFormElement>(null)
    const { publishChange } = useTabs()
    const { toast } = useToast()
    const { can } = useAuth()
    // The authors routes hang off the invoices capability (see app.ts).
    const canWrite = can('invoices', 'write')
    const recordVersion = useDataVersion(id ? `authors:${id}` : '')
    const lock = useEditLock(id ? `author:${id}` : null)

    const [rec, setRec] = useState<AuthorDetail | null>(null)
    const [form, setForm] = useState<Form>(empty)
    const [baseline, setBaseline] = useState<Form>(empty) // last saved/loaded snapshot
    const [busy, setBusy] = useState(false)
    // Der Befund zu einer eingetippten, aber noch nicht übernommenen Nummer. Er gehört zum FELD,
    // nicht zum Datensatz — und verfällt, wenn die Maske ohne Speichern verlassen wird oder
    // jemand weitertippt.
    const [pruefung, setPruefung] = useState<VatCheckOutcome | null>(null)
    // ⚠️ Konnte die IBAN nicht geladen werden, bleibt das Feld GESPERRT statt leer und
    // beschreibbar. Eine Fehlermeldung allein genügt nicht — man überliest sie, wenn das Feld
    // einladend aussieht, und tippt eine IBAN ein, die es längst gibt.
    const [ibanLadefehler, setIbanLadefehler] = useState<'recht' | 'fehler' | null>(null)
    // Der Befund des Format-Knopfes — er BLEIBT stehen, statt als Einblendung zu verschwinden
    // („wie bei der UID mit Icons OK, Cancel und Pending", Betreiber, 12.09.2026).
    // ⚠️ Er merkt sich, WOFÜR er gilt. Wird das Feld danach geändert, ist das Urteil über die
    // alte Zeichenfolge wertlos und die Anzeige fällt auf „nicht geprüft" zurück — dieselbe
    // Regel wie beim UID-Zeichen, das einen Befund zu einer nicht übernommenen Nummer auch
    // nicht als gültig stehen lässt. Nichts davon wird gespeichert: Mod-97 ist jederzeit neu
    // ausrechenbar, ein Datum am Datensatz wäre nur eine zweite Wahrheit.
    const [ibanBefund, setIbanBefund] = useState<{ fuer: string, ok: boolean, grund: string | null } | null>(null)
    // „Erneut versuchen" — zaehlt hoch und stoesst denselben Ladeweg noch einmal an.
    const [ladeVersuch, setLadeVersuch] = useState(0)
    const [error, setError] = useState('')
    const grantedToasted = useRef(false)

    // stableJson, not JSON.stringify: same house rule as the other forms — compare the
    // MEANING, so taking a change back really does clear the ● again.
    const dirty = stableJson(form) !== stableJson(baseline)
    const dirtyRef = useRef(dirty)
    dirtyRef.current = dirty

    // Notify (once) when the edit lock is handed over after waiting.
    useEffect(() => {
        if (lock.justGranted && !grantedToasted.current) {
            grantedToasted.current = true
            toast(T.lockGranted)
        }
    }, [lock.justGranted, toast])

    useEffect(() => {
        if (!id) return
        // Never reload on top of unsaved input: silently replacing what the operator is
        // typing is exactly the loss the tab is supposed to prevent. Our own save clears
        // `dirty` first, so the refresh after saving still comes through.
        if (recordVersion > 0 && dirtyRef.current) return
        // Die IBAN kommt aus einer eigenen Route — sie reist nicht in jeder Autoren-Antwort mit.
        // Beides zusammen laden, damit `baseline` die IBAN kennt: Sonst wäre das Formular
        // sofort „dirty", weil das Feld etwas enthält, das die Baseline nicht hat.
        //
        // ⚠️ Der Fehler wird NICHT geschluckt. Hier stand `.catch(() => '')` — bei einem 403
        // (fehlendes Schreibrecht) oder einem Netzfehler stand ein LEERES IBAN-Feld da, ohne
        // jeden Hinweis. Wer es für „keine hinterlegt" hielt, tippte eine neue ein und
        // überschrieb die bestehende. Seit ein leeres Feld die IBAN LÖSCHT, genügte sogar ein
        // Speichern ohne jede Eingabe.
        //
        // ⚠️ Das ist der eigentliche Zusammenhang: Der stille Rückfall war harmlos, SOLANGE
        // „leer behält" danebenstand. Ich habe die Schutzregel entfernt und den Rückfall
        // behalten — wer eine von beiden anfasst, muss die andere mitdenken.
        Promise.all([getAuthor(id), getAuthorIban(id).then((v) => ({ ok: true as const, v }))
            .catch((e: any) => ({ ok: false as const, status: e?.response?.status as number | undefined }))])
            .then(([a, iban]) => {
                setRec(a)
                const next = { ...toForm(a), iban: iban.ok ? iban.v : '' }
                setForm(next); setBaseline(next)
                // ⚠️ Die geladene IBAN wird SOFORT beurteilt — sonst stünde „nicht geprüft" an
                // einem Wert, über den das System längst geurteilt hat („Es wird ungeprüft
                // angezeigt obwohl die IBAN gültig ist", Betreiber, 12.09.2026). Anders als bei
                // der UID gibt es dafür keinen gespeicherten Befund und braucht auch keinen:
                // Mod-97 ist jederzeit neu ausrechenbar, ein Datum am Datensatz wäre nur eine
                // zweite Wahrheit, die veralten kann.
                //
                // ⛔ UND ES IST NICHT ÜBERFLÜSSIG, obwohl seit dem 09.09.2026 keine ungültige
                // IBAN mehr gespeichert werden kann: Was VORHER hineinkam, wurde ungeprüft
                // übernommen — „DE123" liegt womöglich noch in einem Altdatensatz. Genau die
                // findet dieses Urteil beim Öffnen, statt sie bis zum nächsten SEPA-Lauf
                // unentdeckt zu lassen. Wer hier „ist ja eh gültig" denkt, übersieht sie.
                if (next.iban) void beurteile(next.iban)
                else setIbanBefund(null)
                // Nach Statuscode unterscheiden: Bei 403 hilft nur jemand anderes, bei allem
                // anderen ein zweiter Versuch. Ein Sammeltext schickte ihn zur falschen Abhilfe.
                setIbanLadefehler(iban.ok ? null : (iban.status === 403 ? 'recht' : 'fehler'))
                setError('')
            }).catch((e: any) => setError(e?.response?.data?.error ?? e?.message ?? T.loadError))
    }, [id, recordVersion, ladeVersuch])

    const set = (patch: Partial<Form>) => {
        // Tippt jemand weiter, gilt ein vorheriger Befund nicht mehr — er gehoert zu genau der
        // Nummer, die geprueft wurde.
        if ('vatId' in patch) setPruefung(null)
        setForm((f) => ({ ...f, ...patch }))
    }
    const setAddr = (patch: Partial<Form['address']>) => setForm((f) => ({ ...f, address: { ...f.address, ...patch } }))

    const readOnly = !canWrite
    const locked = !lock.canEdit
    const disabled = locked || busy || readOnly

    // Persist; used by the submit button AND by the tab's close-confirm dialog.
    // ⚠️ Ein abgelehntes Speichern setzt die Baseline NICHT — das Formular bleibt „dirty", und
    // damit bleibt „Übernehmen" klickbar. Das ist beabsichtigt: Wer den Grund der Ablehnung
    // behoben hat, muss es erneut abschicken können. Ein deaktivierter Knopf sperrte die
    // Eingabe ein. Sichtbar wird die Ablehnung im Kasten `notice err` oben, der im
    // NICHT scrollenden `form-top` sitzt — sonst sähe ein abgelehntes Speichern aus wie ein
    // nie abgeschicktes (Betreiber, 09.09.2026).
    async function save(): Promise<SaveResult> {
        if (!id) return { ok: false, error: T.noAuthorLoaded }
        // Required fields and formats from the markup — one check for every form (forms/checkForm.ts).
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setBusy(true); setError('')
        try {
            const res = await updateAuthor(id, {
                salutation: form.salutation, title: form.title,
                displayName: form.displayName, company: form.company, email: form.email,
                address: form.address, vatId: form.vatId,
                // Empty keeps the stored IBAN (the server skips the field) — so an untouched
                // field never wipes payment data.
                iban: form.iban,
                bic: form.bic, accountHolder: form.accountHolder,
                feeType: (form.feeType || undefined) as FeeType | undefined,
                feeValue: form.feeValue === '' ? undefined : Number(form.feeValue),
                // Empty = follows the brand (null) — the contract value lives at the brand since
                // 2026-09-24; a number here is a deviation for this author, 0 included.
                payoutThreshold: form.payoutThreshold === '' ? null : Number(form.payoutThreshold),
                status: form.status, notes: form.notes,
            })
            // Mark clean BEFORE the refetch below is triggered — otherwise the guard above
            // would treat the form as dirty and skip it.
            // ⚠️ Die IBAN wird NICHT mehr geleert: Sie steht offen im Feld und soll dort auch
            // nach dem Speichern stehen. Früher war sie ein Geheimnisfeld, das nach dem
            // Speichern leer sein musste, damit „leer behält" nicht die eigene Eingabe
            // überschreibt — die Regel gilt hier nicht mehr.
            setBaseline({ ...form })
            publishChange('authors', id)
            // Den Datensatz ausdruecklich nachladen, nicht auf den Reload-Effekt hoffen: Der
            // Server setzt beim Speichern Dinge, die das Formular nicht kennt — den
            // Sperrzeitpunkt und den IBAN-Hinweis. Ohne das stand nach dem Blocken weiter
            // „ab dem Speichern …" unter dem Feld, obwohl der Zeitpunkt laengst gesetzt war.
            // Nur `rec`, nie `form` — sonst wuerde eine Eingabe ueberschrieben.
            try {
                setRec(await getAuthor(id))
                // Die IBAN normalisiert zurückholen: Der Server speichert sie ohne Leerzeichen
                // und in Großbuchstaben — das Feld soll zeigen, was wirklich drinsteht.
                const frisch = await getAuthorIban(id)
                setForm((f) => ({ ...f, iban: frisch }))
                setBaseline((b) => ({ ...b, iban: frisch }))
                // Der Befund galt für die getippte Schreibweise; gespeichert wird normalisiert
                // („de02 1203…" → „DE021203…"). Ohne dies stünde nach dem Speichern ○ an einer
                // IBAN, die der Server gerade erst angenommen hat.
                if (frisch) void beurteile(frisch); else setIbanBefund(null)
            } catch { /* Anzeige, nicht der Speichervorgang */ }
            // The VAT id decides whether this author can be paid at all — say so at once.
            if (res?.vat && res.vat.vatId) {
                if (res.vat.valid) toast(fill(T.savedVatConfirmed, { vatId: res.vat.vatId }))
                else if (!res.vat.reachable) toast(T.savedViesUnreachable, 'err')
                else toast(fill(T.savedVatInvalid, { vatId: res.vat.vatId }), 'err')
            } else toast(T.saved)
            return { ok: true }
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setError(msg)
            return { ok: false, error: msg }
        } finally { setBusy(false) }
    }

    // Ask VIES again — an id can be withdrawn later, and VIES has outages. Only the STATUS is
    // refreshed here, never the form: this button must not swallow what is being typed.
    async function recheck() {
        if (!id) return
        setBusy(true)
        try {
            // ⚠️ Die EINGETIPPTE Nummer wird mitgeschickt. Bis zum 10.09.2026 prüfte der Knopf
            // die GESPEICHERTE — wer eine neue eintippte und prüfen ließ, bekam ein Urteil über
            // die alte. Der Betreiber las dreimal „ungültig" über eine Nummer, die er längst
            // ersetzt hatte: „Es macht keinen Sinn diesen Button zu haben, wenn er nicht
            // funktioniert."
            const r = await checkAuthorVat(id, form.vatId)
            const fresh = await getAuthor(id)
            setRec(fresh)
            // Ein Befund zu einer noch nicht übernommenen Nummer wird NICHT gespeichert — sonst
            // stünde am Datensatz ein Urteil über etwas, das dort nicht steht.
            setPruefung(r.saved === false ? r : null)
            if (!r.reachable) toast(T.viesUnreachable, 'err')
            else if (r.saved === false) {
                toast(r.valid
                    ? fill(T.vatValidApply, { vatId: r.vatId })
                    : fill(T.vatInvalid, { vatId: r.vatId }), r.valid ? 'warn' : 'err')
            }
            else toast(r.valid ? fill(T.vatValid, { vatId: r.vatId }) : fill(T.vatInvalid, { vatId: r.vatId }), r.valid ? 'ok' : 'err')
        } catch (e: any) { toast(e?.response?.data?.error ?? T.checkFailed, 'err') }
        finally { setBusy(false) }
    }

    // Die IBAN im FELD prüfen — Betreiber, 12.09.2026: „wie bei der UID Prüfung soll auch die
    // IBAN Prüfung auf das Feld zugreifen." Also `form.iban`, nicht der gespeicherte Wert;
    // derselbe Grund wie beim UID-Knopf oben, sonst ist der Knopf für den häufigsten Fall
    // gesperrt, nämlich eine gerade eingetippte Nummer.
    //
    // ⛔ Anders als bei VIES wird hier NICHTS nachgeladen und NICHTS gespeichert: Mod-97 ist
    // reine Rechnung, es gibt keinen Datensatz-Zustand, der sich dadurch ändern könnte. Der
    // Umweg über den Server dient allein dazu, dass es die Prüfung nur EINMAL gibt — dieselbe
    // Funktion urteilt beim Speichern, beim SEPA-Export und hier.
    // Urteilt über EINE Zeichenfolge und merkt sich, wofür das Urteil gilt. Von drei Stellen
    // benutzt: beim Laden, nach dem Speichern und vom Knopf. Eine Funktion, damit die drei
    // nicht auseinanderlaufen — der Knopf sagt es zusätzlich als Einblendung, das Zeichen
    // bleibt stehen.
    async function beurteile(kandidat: string) {
        try {
            const r = await checkIbanFormat(kandidat)
            setIbanBefund({ fuer: kandidat, ok: r.ok, grund: r.error })
            return r
        } catch { setIbanBefund(null); return null }
    }

    async function ibanPruefen() {
        setBusy(true)
        try {
            const r = await beurteile(form.iban)
            if (!r) throw new Error(T.checkFailed)
            // ⚠️ „Format gültig", nicht „IBAN gültig". Der Unterschied ist der ganze Punkt:
            // Mod-97 sagt, dass die Nummer in sich stimmig ist — nicht, dass es das Konto gibt
            // und schon gar nicht, wem es gehört. Eine gültige IBAN des falschen Empfängers
            // besteht diese Prüfung anstandslos. Wer „gültig" liest und „das Geld kommt an"
            // versteht, wäre schlechter dran als ohne den Knopf.
            toast(r.ok
                ? fill(T.formatValid, { iban: r.iban })
                : fill(T.formatInvalid, { reason: r.error }), r.ok ? 'ok' : 'err')
        } catch (e: any) { toast(e?.response?.data?.error ?? T.checkFailed, 'err') }
        finally { setBusy(false) }
    }

    // ⚠️ Die IBAN braucht keinen eigenen „Entfernen"-Weg mehr. Seit sie offen im Feld steht
    // (09.09.2026), löscht sie, wer sie leert und speichert — sichtbar und ohne Umweg.
    // Der frühere Dialog samt `DELETE /authors/:id/iban` stammt aus der Zeit, als das Feld
    // maskiert war: Was man nicht sieht, kann man nicht gezielt leeren, also brauchte es eine
    // eigene Geste. Die Route bleibt bestehen (getestet, von außen aufrufbar), die Maske
    // benutzt sie nicht mehr.
    // ── Freigeben: kein Dialog mehr, sondern eine Auskunft ───────────────────────────────
    // Die Verkäufe der Sperrzeit werden beim Freigeben immer zurückgehalten — das ist der einzige
    // Normalfall (Betreiber, 09.09.2026). Gefragt wird nicht mehr: „Was ist, wenn es Streit um
    // die Gebühren gegeben hat?" lässt sich mit einem Alles-oder-nichts-Schalter nicht
    // beantworten, weil meist nur ein TEIL der Sperrzeit strittig ist. Einzelne Zeilen holt man
    // danach über die Verkaufsliste zurück.
    // ⚠️ Gesagt wird es trotzdem, mit Betrag UND mit dem Weg dorthin — ein Hinweis, der sagt,
    // dass etwas liegen bleibt, aber nicht wo man es holt, ist eine Sackgasse.
    const [released, setReleased] = useState<BlockedPeriod | null>(null)
    // Ein Altbestand ohne Zeitpunkt hat keine Sperrzeit — da bleibt nichts liegen.
    const isRelease = !!rec && rec.status !== 'active' && !!rec.blockedAt && form.status === 'active'

    async function submitWithNotice(): Promise<SaveResult> {
        if (!isRelease || !id) return save()
        // Die Zahlen VOR dem Speichern holen: danach ist der Sperrzeitpunkt geleert und die
        // Vorschau meldete null.
        let info: BlockedPeriod | null = null
        try { info = await getBlockedPeriod(id) } catch { /* die Auskunft ist nicht der Zweck */ }
        const res = await save()
        if (res.ok && info && info.count > 0) setReleased(info)
        return res
    }

    function submit(e: React.FormEvent) { e.preventDefault(); void submitWithNotice() }
    useDirtyGuard(tabId, dirty && !locked && !readOnly, submitWithNotice)

    if (!rec) return <div className="form-page"><h1>{T.author}</h1><p className="muted">{error || T.loading}</p></div>

    // autoComplete="off" sits on the form AND on the individual fields: this records ANOTHER
    // person's data, so every suggestion the browser makes is wrong by definition.
    return (
        <form ref={formRef} noValidate onSubmit={submit} className="form-page" autoComplete="off">
            <div className="form-top">
            <div className="form-header">
                <h1>{readOnly ? fill(T.titleView, { name: rec.displayName }) : fill(T.titleEdit, { name: rec.displayName })}</h1>
                {!readOnly && <button type="submit" disabled={disabled || !dirty}>{busy ? '…' : T.apply}</button>}
            </div>

            {readOnly && <div className="notice">{T.readOnlyNotice}</div>}
            {locked && (
                <div className="notice warn">
                    {T.lockedBefore}
                    {lock.position > 0 ? fill(T.lockedPosition, { position: lock.position }) : ''}{T.lockedAfter}
                </div>
            )}
            {error && <div className="notice err">{error}</div>}
            </div>

            <fieldset disabled={disabled} className="form-body" style={{ border: 0, padding: 0, margin: 0 }}>
                <div className="panel">
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 140 }}><label>{T.salutation}</label>
                            <select name="author-salutation" value={form.salutation}
                                onChange={(e) => set({ salutation: e.target.value as Form['salutation'] })}>
                                <option value="">{T.salutationNone}</option>
                                {AUTHOR_SALUTATIONS.map((c) => <option key={c} value={c}>{AUTHOR_SALUTATION_LABELS[c]}</option>)}
                            </select></div>
                        <div className="field" style={{ maxWidth: 200 }}><label>{T.title}</label>
                            <input type="text" name="author-title" autoComplete="off" maxLength={AUTHOR_TITLE_MAX_LENGTH}
                                placeholder={T.titlePlaceholder}
                                value={form.title} onChange={(e) => set({ title: e.target.value })} /></div>
                        <div className="field"><label>{T.name}</label>
                            <input type="text" name="author-name" autoComplete="off" required
                                value={form.displayName} onChange={(e) => set({ displayName: e.target.value })} /></div>
                        <div className="field"><label>{T.company}</label>
                            <input type="text" name="author-company" autoComplete="off"
                                value={form.company} onChange={(e) => set({ company: e.target.value })} /></div>
                        <div className="field" style={{ maxWidth: 120 }}>
                            <label>{T.wpId}<InfoTip>{T.wpIdTip}</InfoTip></label>
                            <input type="text" name="author-external-ref" value={rec.externalRef} readOnly disabled /></div>
                    </div>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field"><label>{T.email}</label>
                            <input type="email" name="author-email" autoComplete="off"
                                value={form.email} onChange={(e) => set({ email: e.target.value })} /></div>
                        <div className="field" style={{ maxWidth: 200 }}><label>{T.status}</label>
                            <select name="author-status" value={form.status}
                                onChange={(e) => set({ status: e.target.value as Form['status'] })}>
                                <option value="active">{T.statusActive}</option>
                                <option value="blocked">{T.statusBlocked}</option>
                            </select>
                            <span className="hint">{statusHint(form.status, rec)}</span>
                        </div>
                    </div>
                </div>

                <div className="panel">
                    <h3>{T.address}</h3>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field"><label>{T.street}</label>
                            <input type="text" name="author-street" autoComplete="off"
                                value={form.address.line1} onChange={(e) => setAddr({ line1: e.target.value })} /></div>
                        <div className="field"><label>{T.line2}</label>
                            <input type="text" name="author-street2" autoComplete="off"
                                value={form.address.line2} onChange={(e) => setAddr({ line2: e.target.value })} /></div>
                    </div>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 140 }}><label>{T.postalCode}</label>
                            <input type="text" name="author-zip" autoComplete="off"
                                value={form.address.postalCode} onChange={(e) => setAddr({ postalCode: e.target.value })} /></div>
                        <div className="field"><label>{T.city}</label>
                            <input type="text" name="author-city" autoComplete="off"
                                value={form.address.city} onChange={(e) => setAddr({ city: e.target.value })} /></div>
                        <div className="field" style={{ maxWidth: 220 }}>
                            <label>{T.country}</label>
                            {/* Picked, not typed: a country code decides the reverse charge, and a
                                typo in a free-text field is invisible until a settlement refuses.
                                All countries since 2026-10-23 (was EU only); the server checks the list. */}
                            <CountryPicker name="author-country" value={form.address.country}
                                onChange={(country) => setAddr({ country })} />
                        </div>
                    </div>
                </div>

                <div className="panel">
                    <h3>{T.tax}<InfoTip>
                        {T.taxTip}
                    </InfoTip></h3>
                    <div className="field" style={{ maxWidth: 420 }}>
                        <label>{T.vatId}<span className="hint">{T.vatIdRequired}</span></label>
                        <div className="row" style={{ gap: 8 }}>
                            {/* The field this whole guard exists for: an anonymous text input right
                                below a type="email" one is what browsers happily fill with the
                                e-mail address. A `name` matching no standard field plus off keeps
                                the heuristics away. */}
                            <input type="text" name="author-vat-id" autoComplete="off" placeholder={T.vatIdPlaceholder}
                                value={form.vatId} onChange={(e) => set({ vatId: e.target.value })} />
                            {/* ⚠️ `form.vatId`, nicht `rec.vatId`: Man muss prüfen können, was man
                                gerade eingetippt hat — sonst ist der Knopf für den häufigsten Fall
                                gesperrt, nämlich eine neue Nummer. */}
                            <button type="button" className="secondary" disabled={busy || !form.vatId.trim()}
                                title={T.vatCheckTitle}
                                onClick={() => void recheck()}>
                                {T.vatCheck}
                            </button>
                            {/* ⚠️ DIE FARBE SAGT NUR EINES: gültig oder nicht. Ein erster Versuch
                                färbte „geprüft, aber noch nicht übernommen" orange — und bei einer
                                GÜLTIGEN Nummer stand dort ein farbiger Punkt, den jeder als
                                „falsch" liest. Der Betreiber am 10.09.2026: „OK wenn valid nicht
                                ein roter Punkt !!!" Er hat recht: Eine Farbe kann nur eine
                                Bedeutung tragen, und „gültig" ist die wichtigere.
                                ⚠️ Und unter dem Feld steht GAR NICHTS mehr: „es ist immer noch
                                ein Hinweis unter der UID Nummer. Fehlermeldungen sind ok. Alles
                                andere weg damit." (Betreiber, 10.09.2026, nach dreimaliger
                                Bitte). Das Zeichen trägt den Zustand, der vollständige Satz steht
                                im `title` — die Auskunft ist erreichbar, ohne Fläche zu kosten.
                                Ein Zustand gehört nicht zweimal dargestellt. */}
                            {(() => {
                                const abweichend = normalizeVatId(form.vatId) !== normalizeVatId(rec.vatId ?? '')
                                if (!abweichend) {
                                    const z = vatZeichen(rec)
                                    return <span className="vat-mark" style={{ color: z.farbe }} title={z.titel}>{z.zeichen}</span>
                                }
                                // Eingetippt, noch nicht übernommen.
                                if (!pruefung) {
                                    return (
                                        <span className="vat-mark" style={{ color: 'var(--muted)' }}
                                            title={fill(T.vatNotCheckedTitle, { stored: rec.vatId || T.noNumber, verdict: vatSatz(rec) })}>○</span>
                                    )
                                }
                                if (pruefung.reachable === false) {
                                    return (
                                        <span className="vat-mark" style={{ color: 'var(--muted)' }}
                                            title={fill(T.vatUnreachableTitle, { vatId: pruefung.vatId })}>○</span>
                                    )
                                }
                                return (
                                    <>
                                        <span className="vat-mark" style={{ color: pruefung.valid ? 'var(--ok)' : 'var(--danger)' }}
                                            title={pruefung.valid
                                                ? fill(T.vatConfirmedPendingTitle, { vatId: pruefung.vatId })
                                                : fill(T.vatRejectedTitle, { vatId: pruefung.vatId })}>
                                            {pruefung.valid ? '✓' : '✗'}
                                        </span>
                                    </>
                                )
                            })()}
                        </div>
                    </div>
                </div>

                <div className="panel">
                    <h3>{T.bank}</h3>
                    <div style={{ maxWidth: 520 }}>
                        {/* ⚠️ Die IBAN steht hier OFFEN — Entscheidung des Betreibers vom
                            09.09.2026: „so weiss man nie ob man sich vertippt hat." Sie bleibt
                            verschlüsselt gespeichert; nur die Anzeige ändert sich.
                            Sie wird über eine eigene Route nachgeladen, damit sie nicht in jeder
                            Autoren-Antwort mitreist.

                            ⚠️ Damit gilt hier die Regel „leer beim Speichern BEHÄLT" NICHT mehr,
                            anders als bei den Geheimnisfeldern. Die Regel schützt davor, ein
                            Feld zu leeren, das man gar nicht sehen kann — wer eine sichtbare
                            IBAN löscht und speichert, tut es absichtlich. Das Gegenteil wäre
                            schlimmer: ein leeres Feld, das beim Speichern die alte IBAN
                            zurückholt, ohne dass jemand versteht warum. */}
                        <div className="field">
                            <label>{T.iban}</label>
                            {/* Dieselbe Gestalt wie beim UID-Feld oben: Eingabe und Knopf in einer
                                `.row`, damit sich beide gleich anfühlen — so hat der Betreiber es
                                verlangt („wie bei der UID Prüfung"). */}
                            <div className="row" style={{ gap: 8 }}>
                            <input type="text" name="author-iban" className="iban-input" autoComplete="off" placeholder={T.ibanPlaceholder}
                                disabled={!!ibanLadefehler}
                                value={form.iban} onChange={(e) => set({ iban: e.target.value })} />
                            {/* ⚠️ `form.iban`, nicht der gespeicherte Wert — man muss prüfen können,
                                was gerade dasteht. Und ⚠️ die Beschriftung sagt „Format prüfen",
                                nicht „Prüfen": Beim UID-Knopf daneben heißt geprüft wirklich
                                „existiert in der EU-Datenbank", hier nur „Prüfziffer stimmt". Zwei
                                gleich beschriftete Knöpfe mit ungleicher Aussagekraft wären die
                                schlechtere Art von Konsistenz. */}
                            <button type="button" className="secondary"
                                disabled={busy || !form.iban.trim() || !!ibanLadefehler}
                                title={T.ibanCheckTitle}
                                onClick={() => void ibanPruefen()}>
                                {T.ibanCheck}
                            </button>
                            {/* Dasselbe Zeichen wie beim UID-Feld: ✓ / ✗ / ○, Farbe aus dem Haus.
                                ⚠️ Der Vorbehalt steht im `title`, nicht daneben — „gültig" heißt
                                hier NUR, dass Prüfziffer und Länge stimmen. Ein grünes Häkchen
                                ohne diesen Satz wäre das Versprechen, das der Knopf nicht halten
                                kann. */}
                            {(() => {
                                const gilt = ibanBefund && ibanBefund.fuer === form.iban
                                if (!gilt) {
                                    return <span className="vat-mark" style={{ color: 'var(--muted)' }}
                                        title={!form.iban.trim()
                                            ? T.ibanNone
                                            : ibanBefund
                                                ? T.ibanChangedSinceCheck
                                                : T.ibanNotChecked}>○</span>
                                }
                                return <span className="vat-mark"
                                    style={{ color: ibanBefund!.ok ? 'var(--ok)' : 'var(--danger)' }}
                                    title={ibanBefund!.ok
                                        ? T.ibanFormatOk
                                        : fill(T.formatInvalid, { reason: ibanBefund!.grund })}>
                                    {ibanBefund!.ok ? '✓' : '✗'}
                                </span>
                            })()}
                            </div>
                            {ibanLadefehler === 'recht' ? (
                                <span className="hint" style={{ color: 'var(--danger)' }}>
                                    {T.ibanNoRight}
                                </span>
                            ) : ibanLadefehler === 'fehler' ? (
                                <span className="hint" style={{ color: 'var(--danger)' }}>
                                    {T.ibanLoadFailed}{' '}
                                    <button type="button" className="link-btn"
                                        onClick={() => setLadeVersuch((n: number) => n + 1)}>{T.retry}</button>
                                </span>
                            ) : (
                                <span className="hint">
                                    {T.ibanHint}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 200 }}><label>{T.bic}</label>
                            <input type="text" name="author-bic" autoComplete="off"
                                value={form.bic} onChange={(e) => set({ bic: e.target.value })} /></div>
                        <div className="field"><label>{T.accountHolder}</label>
                            <input type="text" name="author-account-holder" autoComplete="off"
                                value={form.accountHolder} onChange={(e) => set({ accountHolder: e.target.value })} /></div>
                    </div>
                </div>

                <div className="panel">
                    <h3>{T.settlement}</h3>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 180 }}>
                            <label>{T.feeType}<InfoTip>{T.feeTypeTip}</InfoTip></label>
                            <select name="author-fee-type" value={form.feeType}
                                onChange={(e) => set({ feeType: e.target.value as Form['feeType'] })}>
                                <option value="">{T.feeBrandDefault}</option>
                                <option value="percent">{T.percent}</option>
                                <option value="fixed">{T.fixed}</option>
                            </select>
                        </div>
                        <div className="field" style={{ maxWidth: 140 }}>
                            <label>{T.feeValue}{form.feeType === 'fixed' ? `(${moneySign()})` : form.feeType === 'percent' ? '(%)' : ''}</label>
                            <input type="number" name="author-fee-value" step="0.01" min="0" disabled={!form.feeType}
                                value={form.feeValue} onChange={(e) => set({ feeValue: e.target.value })} /></div>
                        <div className="field" style={{ maxWidth: 180 }}>
                            <label>{fill(T.payoutThreshold, { sign: moneySign() })}<InfoTip>{T.payoutThresholdTip}</InfoTip></label>
                            <input type="number" name="author-payout-threshold" step="0.01" min="0" placeholder={T.payoutThresholdPlaceholder}
                                value={form.payoutThreshold} onChange={(e) => set({ payoutThreshold: e.target.value })} /></div>
                        <div className="field" style={{ maxWidth: 120 }}>
                            <label>{T.items}<InfoTip>{T.itemsTip}</InfoTip></label>
                            <input type="text" name="author-item-count" value={rec.itemCount} readOnly disabled /></div>
                    </div>
                    <div className="field"><label>{T.notes}</label>
                        <textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></div>
                </div>
            </fieldset>

            {/* Ein reiner Hinweis, deshalb ohne `onCancel`: Hier gibt es nichts abzulehnen —
                die Verkäufe SIND bereits zurückgehalten, der Dialog sagt es nur. Zwei Knöpfe
                versprächen eine Wahl, die es nicht gibt (Betreiber, 09.09.2026). */}
            <ConfirmDialog
                open={!!released}
                title={T.releasedTitle}
                message={released ? (
                    <>
                        {T.released.quoteOpen}{rec.displayName}{T.released.activeSince}{' '}
                        <b>{released.blockedAt ? new Date(released.blockedAt).toLocaleDateString('de-DE') : '—'}</b>{' '}
                        {plural(released.count, T.released.remainVerb)}{' '}
                        <b>{fill(plural(released.count, T.released.salesCount), { count: released.count })}</b>{T.released.over}{' '}
                        <b>{formatMoney(released.payout)}</b>{' '}
                        {SALE_STATE_LABELS.excluded}{T.released.dash}{plural(released.count, T.released.pronoun)}{T.released.notPaid}<br />
                        <span className="hint">
                            {T.released.hintBefore}<b>{T.released.hintBold}</b>{T.released.hintAfter}
                        </span>
                    </>
                ) : null}
                confirmLabel={T.understood}
                onConfirm={() => setReleased(null)}
            />

        </form>
    )
}
